import React, { ButtonHTMLAttributes, MutableRefObject } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import media from '../../constants/media';
import theme from '../../constants/theme';

const buttonSizes = {
	small: {
		height: 40,
		radius: 8,
		padding: 16,
		font: 12,
		icon: 16,
	},
	big: {
		height: 64,
		radius: 8,
		padding: 26,
		font: 18,
		icon: 24,
	},
};

const buttonTypes = {
	primary: {
		backgroundColor: theme.colorsV2.mainRed,
		textColor: theme.colors.white,
		fontWeight: 700,
		borderColor: theme.colorsV2.mainRed,
		disabledBackgroundColor: theme.colorsV2.grey5,
		disabledBorderColor: theme.colorsV2.grey5,
		disabledTextColor: theme.colors.white,
		hoverBackgroundColor: theme.colorsV2.secondaryRed,
		hoverTextColor: theme.colors.white,
		hoverBorderColor: theme.colorsV2.secondaryRed,
		activeBackgroundColor: theme.colorsV2.lightRed,
		activeTextColor: theme.colors.white,
		activeBorderColor: theme.colorsV2.lightRed,
		focusBackgroundColor: theme.colorsV2.mainRed,
		focusBorderColor: theme.colorsV2.secondaryRed,
		focusBorderWidth: 2,
		mixBlendMode: 'multiply',
	},
	secondary: {
		backgroundColor: theme.colors.white,
		textColor: theme.colorsV2.mainRed,
		fontWeight: 500,
		borderColor: theme.colorsV2.mainRed,
		disabledBackgroundColor: theme.colorsV2.grey5,
		disabledBorderColor: theme.colorsV2.grey5,
		disabledTextColor: theme.colors.white,
		hoverBackgroundColor: theme.colors.white,
		hoverTextColor: theme.colorsV2.secondaryRed,
		hoverBorderColor: theme.colorsV2.secondaryRed,
		activeBackgroundColor: theme.colors.white,
		activeTextColor: theme.colorsV2.lightRed,
		activeBorderColor: theme.colorsV2.lightRed,
		focusBackgroundColor: `${theme.colorsV2.mainRed}1A`,
		focusBorderColor: theme.colorsV2.mainRed,
		focusBorderWidth: 0,
		mixBlendMode: 'multiply',
	},
};

type TButtonSize = typeof buttonSizes.small;
type TButtonSizeOption = keyof typeof buttonSizes;

type TButtonType = typeof buttonTypes.primary;
type TButtonTypeOption = keyof typeof buttonTypes;

const ButtonTitle = styled.span`
	font-family: ${theme.fonts.Arimo};
	white-space: nowrap;
	transition: 0.3s ease;
`;

const Wrapper = styled.div`
	${({ right }) =>
		right &&
		`
		width: 100%;
		display: flex;
		justify-content: flex-end;
	`}
`;

const ButtonRoot = styled.button<{
	$buttonType: TButtonType;
	$buttonSize: TButtonSize;
	size: TButtonSizeOption;
	disabled?: boolean;
}>`
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: ${({ $buttonSize }) => $buttonSize.radius}px;
	height: ${({ $buttonSize }) => $buttonSize.height}px;
	padding-right: ${({ $buttonSize }) => $buttonSize.padding}px;
	padding-left: ${({ $buttonSize }) => $buttonSize.padding}px;
	background-color: ${({ disabled, $buttonType }) =>
		disabled ? $buttonType.disabledBackgroundColor : $buttonType.backgroundColor};
	cursor: ${({ disabled }) => !disabled && 'pointer'};
	transition: 0.3s ease;
	border-color: ${({ disabled, $buttonType }) =>
		disabled ? $buttonType.disabledBorderColor : $buttonType.borderColor};
	border-style: solid;
	border-width: 1px;

	${ButtonTitle} {
		color: ${({ disabled, $buttonType }) => (disabled ? $buttonType.disabledTextColor : $buttonType.textColor)};
		font-weight: ${({ $buttonType }) => $buttonType.fontWeight};
		font-size: ${({ $buttonSize }) => $buttonSize.font}px;
	}

	${({ right }) =>
		right &&
		`
		width: 100%;
		justify-content: flex-end;
	`}

	&:active {
		background-color: ${({ disabled, $buttonType }) => !disabled && $buttonType.activeBackgroundColor};
		color: ${({ disabled, $buttonType }) => !disabled && $buttonType.activeTextColor};
		border-color: ${({ disabled, $buttonType }) => !disabled && $buttonType.activeBorderColor};

		${ButtonTitle} {
			color: ${({ disabled, $buttonType }) => !disabled && $buttonType.activeTextColor};
		}

		svg path {
			stroke: ${({ disabled, $buttonType }) => !disabled && $buttonType.activeTextColor};
		}
	}

	&:focus-visible {
		box-shadow: ${({ $buttonType }) =>
			`inset 0 0 0 ${$buttonType.focusBorderWidth}px ${$buttonType.focusBorderColor}`};
		background-color: ${({ $buttonType }) => $buttonType.focusBackgroundColor};
		border-color: ${({ $buttonType }) => $buttonType.focusBorderColor};
		outline: none;
	}

	${media.tablet`
		position: relative;
		border-color: ${({ disabled, $buttonType }) => (disabled ? $buttonType.disabledBorderColor : $buttonType.borderColor)};
	`}

	${media.desktopSmall`
		&:hover {
			background-color: ${({ disabled, $buttonType }) => !disabled && $buttonType.hoverBackgroundColor};
			border-color: ${({ disabled, $buttonType }) => !disabled && $buttonType.hoverBorderColor};
			box-shadow: ${({ disabled }) => !disabled && '0 0 30px rgba(0, 0, 0, 0.08)'};

			${ButtonTitle} {
				color: ${({ disabled, $buttonType }) => !disabled && $buttonType.hoverTextColor};
			}
		}
	`}
`;

type TProps = {
	buttonRef?: MutableRefObject<HTMLButtonElement | HTMLAnchorElement | null>;
	title: string;
	type?: TButtonTypeOption;
	htmlType?: ButtonHTMLAttributes<any>['type'];
	size?: TButtonSizeOption;
	disabled?: boolean;
	hidden?: boolean;
	onClick?: () => void;
	className?: string;
	href?: string;
	target?: string | null;
	alignRight?: boolean;
};

const Button = ({
	buttonRef,
	title,
	type = 'primary',
	htmlType,
	size = 'big',
	disabled,
	hidden,
	onClick,
	className,
	href,
	target,
	alignRight,
}: TProps) => {
	if (!href) {
		return (
			<Wrapper right={alignRight}>
				<ButtonRoot
					ref={buttonRef}
					onClick={!disabled && onClick ? onClick : undefined}
					$buttonType={buttonTypes[type]}
					$buttonSize={buttonSizes[size]}
					size={size}
					type={htmlType}
					disabled={disabled}
					aria-hidden={hidden}
					aria-label={title}
					tabIndex={hidden ? -1 : 0}
					className={className}>
					<ButtonTitle aria-hidden="true">{title}</ButtonTitle>
				</ButtonRoot>
			</Wrapper>
		);
	}
	return (
		<ButtonRoot
			{...(!href.startsWith('http') &&
				(!href.endsWith('pdf') || !href.endsWith('exe') || !href.endsWith('zip')) && {
					as: Link,
					to: href,
					target: target,
				})}
			{...((href.startsWith('http') || href.endsWith('pdf') || href.endsWith('exe') || href.endsWith('zip')) && {
				as: 'a',
				href: href,
				target: target,
			})}
			ref={buttonRef}
			$buttonType={buttonTypes[type]}
			$buttonSize={buttonSizes[size]}
			size={size}
			disabled={disabled}
			aria-hidden={hidden}
			aria-label={title}
			tabIndex={hidden ? -1 : 0}
			className={className}>
			<ButtonTitle aria-hidden="true">{title}</ButtonTitle>
		</ButtonRoot>
	);
};

export default Button;
